/**
 * Copyright 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import jsonConfig from '../settings.json';

export const auth0 = {
  domain: jsonConfig.auth0Domain,
  clientId: jsonConfig.auth0ClientId,
  redirectUri: jsonConfig.redirectUri,
};

const API = {
  graphql_endpoint: jsonConfig.graphqlEndpoint,
};

export const amplifyConfig = {
  API,
  auth0,
};

export const inputsConfig = {
  plans: [
    {
      id: 'name',
      name: 'Name',
      type: 'text',
      required: false,
    },
    {
      id: 'owner',
      name: 'Customer',
      type: 'text',
      required: true,
    },
    {
      id: 'description',
      name: 'Description',
      type: 'text',
      required: false,
    },
    {
      id: 'cron_window_start',
      name: 'Cron Window Start',
      type: 'text',
      required: true,
    },
    {
      id: 'window_length',
      name: 'Patching window duration [h]',
      type: 'number',
      required: true,
      description: 'Length of the patching window in hours. Min = 1h, Max = 24h',
    },
    {
      id: 'machines',
      name: 'Machines',
      type: 'machines',
      required: false,
      description: "List of machine id's to update.",
    },
    {
      id: 'on_hold_start',
      name: 'On Hold Start Date',
      type: 'date',
      required: false,
    },
    {
      id: 'on_hold_end',
      name: 'On Hold End Date',
      type: 'date',
      required: false,
    },
    {
      id: 'notification_groups',
      name: 'Notification Groups',
      type: 'groups',
      required: false,
      description: "List of notification groups id's.",
    },
    {
      id: 'dry_run',
      name: 'Dry Run',
      type: 'checkbox',
      required: false,
      description: 'Set true to list available updates without updating.',
    },
    {
      id: 'manual_approval',
      name: 'Manual approval',
      type: 'checkbox',
      required: false,
      description:
        'Schedule events from this plan for execution only after they have been manually approved.',
    },
    {
      id: 'parallel',
      name: 'Parallel',
      type: 'number',
      required: false,
      description: 'How many updates in parallel (0-all, 1-in sequence, 2- 2 in parallel etc).',
    },
  ],
  notification_groups: [
    {
      id: 'owner',
      name: 'Customer',
      type: 'text',
      required: true,
    },
    {
      id: 'name',
      name: 'Name',
      type: 'text',
      required: false,
    },
    {
      id: 'notifications',
      name: 'Notifications',
      type: 'notifications',
      required: false,
    },
    {
      id: 'events',
      name: 'Notify about events',
      type: 'notification_events',
      required: false,
      description:
        'Select events you want to be notififed about. If you do not select any events you will be notified about every one.',
    },
  ],
  machines: [
    {
      id: 'id',
      name: 'ID',
      type: 'text',
      required: false,
      description: "Machine ID, if not provided it'll be auto-generated",
    },
    {
      id: 'name',
      name: 'Name',
      type: 'text',
      required: false,
    },
    {
      id: 'owner',
      name: 'Customer',
      type: 'text',
      required: true,
    },
    {
      id: 'description',
      name: 'Description',
      type: 'text',
      required: false,
    },
    {
      id: 'access',
      name: 'Access',
      type: 'object',
      data: [
        {
          id: 'access.ssm_machine_id',
          name: 'SSM instance id',
          type: 'text',
          required: false,
        },
        {
          id: 'access.iam_role',
          name: 'IAM role',
          type: 'text',
          required: false,
          description: 'IAM role arn to assume for SSM (required in normal mode).',
        },
        {
          id: 'access.region',
          name: 'Instance region',
          type: 'text',
          required: false,
        },
      ],
    },
    {
      id: 'type',
      name: 'OS Type',
      type: 'select',
      options: ['LIN', 'WIN'],
      required: true,
    },
    {
      id: 'subtype',
      name: 'OS subtype',
      type: 'select',
      options: ['UBUNTU', 'AMAZON', 'CENTOS', 'REDHAT', 'DEBIAN', 'KALI', 'SUSE'],
      required: false,
    },
    {
      id: 'allow_reboot',
      name: 'Allow Reboot',
      type: 'checkbox',
      required: false,
      description: 'Set true to reboot after patching.',
    },
    {
      id: 'update_category',
      name: 'Update Category',
      type: 'text',
      required: false,
      description:
        'Default is all, Works for Windows, you can specify coma separated list: CriticalUpdates,DefinitionUpdates,Drivers,FeaturePacks,SecurityUpdates,ServicePacks,UpdateRollups,Updates.',
    },
    {
      id: 'update_severity',
      name: 'Update Severity',
      type: 'text',
      required: false,
      description:
        'Default is: Critical,Important,Moderate, Works for Windows, you can specify coma separated list: Critical,Important,Moderate,Low,Unspecified.',
    },
    {
      id: 'published_days_old',
      name: 'Days since update',
      type: 'text',
      required: false,
    },
    {
      id: 'include_kbs',
      name: 'KBS documents to include',
      type: 'list',
      required: false,
      description:
        'Comma separated list of KBS documents to include during patching, i.e. KB9876543,KB9876123,KB1234567',
    },
    {
      id: 'exclude_kbs',
      name: 'KBS documents to exclude',
      type: 'list',
      required: false,
      description:
        'Comma separated list of KBS documents to exclude during patching, i.e. KB9876543,KB9876123,KB1234567',
    },
    {
      id: 's3_custom_script',
      name: 'Custom update command',
      type: 'text',
      required: false,
    },
  ],
  activations: [
    {
      id: 'default_instance_name',
      name: 'Instance Default Name',
      type: 'text',
      required: true,
    },
    {
      id: 'registration_limit',
      name: 'Registration Limit',
      type: 'text',
      required: true,
    },
    {
      id: 'description',
      name: 'Description',
      type: 'text',
      required: 'false',
    },
  ],
  role: [
    {
      id: 'name',
      name: 'Name',
      type: 'text',
      required: false,
    },
    {
      id: 'rules',
      name: 'Rules',
      type: 'Rules',
      required: true,
    },
  ],
};

export const PATCHING_STATES_MAPPING = {
  NEW: { name: 'upcoming', className: 'is-info' },
  IN_PROGRESS: { name: 'in progress', className: 'is-warning' },
  DONE: { name: 'success', className: 'is-success' },
  DONE_MANUAL: { name: 'success manual', className: 'is-success' },
  DONE_PARTIAL: { name: 'partial success', className: 'is-warning' },
  DONE_ERROR: { name: 'failed', className: 'is-danger' },
  DONE_ERROR_MANUAL: { name: 'failed manual', className: 'is-danger' },
  DELETED: { name: 'deleted', className: 'is-danger' },
  UNKNOWN: { name: 'not defined', className: '' },
  NOT_APPROVED: { name: 'not approved', className: 'is-warning' },
  APPROVED: { name: 'approved', className: 'is-info' },
  DONE_ERROR_BASELINE: { name: 'baseline failed', className: 'is-danger' },
  BASELINE_BUILDING: { name: 'baseline building', className: 'is-warning' },
};

export const MACHINE_STATES_MAPPING = {
  Not_available: { name: 'not available', className: 'is-warning' },
  Access_error: { name: 'access error', className: 'is-warning' },
  In_progress: { name: 'in progress', className: 'is-light' },
  In_progress_manual: { name: 'in progress manual', className: 'is-light' },
  Rebooting_manual: { name: 'rebooting manual', className: 'is-light' },
  Success: { name: 'success', className: 'is-success' },
  Success_manual: { name: 'success manual', className: 'is-success' },
  Success_no_updates_available: {
    name: 'success, no updates',
    className: 'is-success',
  },
  TimedOut: { name: 'timed out', className: 'is-warning' },
  Failed: { name: 'failed', className: 'is-warning' },
  Failed_manual: { name: 'failed manual', className: 'is-warning' },
  Waiting: { name: 'waiting', className: 'is-black' },
  Error: { name: 'error', className: 'is-danger' },
  Omitted: { name: 'omitted', className: 'is-danger' },
  Not_in_time_window: { name: 'not in the time window', className: 'is-info' },
  Pre_hook_fail: { name: 'pre hook failed', className: 'is-warning' },
  Pre_host_hook_fail: {
    name: 'instance pre hook failed',
    className: 'is-warning',
  },
  Post_host_hook_fail: {
    name: 'instance post hook failed',
    className: 'is-warning',
  },
  Not_reached_due_to_hook_fail: {
    name: 'not reached due to hook fail',
    className: 'is-warning',
  },
  Command_generate_fail: {
    name: 'command generate fail',
    className: 'is-warning',
  },
  Missing_information: { name: 'missing information', className: 'is-info' },
  Upcoming: { name: 'upcoming', className: 'is-info' },
  Plan_missing: { name: 'plan missing', className: 'is-info' },
  Excluded: { name: 'excluded', className: 'is-warning' },
};

export const MACHINE_TYPES_MAPPING = {
  Windows: { name: 'Windows', icon: 'fa-windows' },
  Linux: { name: 'Linux', icon: 'fa-linux' },
};

export const NOTIFICATION_EVENTS_MAPPING = {
  EVENT_NEW: { name: 'New event created' },
  ERROR: { name: 'Error' },
  INCOMING_PATCHING: { name: 'Incoming patching' },
  NOT_APPROVED_PATCHING: { name: 'Not approved patching' },
  PLAN_MODIFICATION: { name: 'Plan modification' },
  PLAN_NEW: { name: 'New plan created' },
  PLAN_DELETED: { name: 'Deleted Plan' },
  PIPELINE_MODIFICATION: { name: 'Pipeline modification' },
  PIPELINE_NEW: { name: 'New pipeline created' },
  PIPELINE_DELETED: { name: 'Deleted Pipeline' },
  PATCHING_START: { name: 'Patching started' },
  PATCHING_FINISH: { name: 'Patching finished' },
  PARTIAL_PATCHING_START: { name: 'Partial patching started' },
  PARTIAL_PATCHING_FINISH: { name: 'Partial patching finished' },
  MACHINE_PATCHING_START: { name: 'Instance update started' },
  MACHINE_PATCHING_FINISH: { name: 'Instance update finished' },
  MACHINE_ACCESS_ERR: { name: 'Instance accees error' },
  MACHINE_NOT_IN_WINDOW: { name: 'Instance not in time window' },
  HOOK_START: { name: 'Patching hook started' },
  HOOK_FINISH: { name: 'Patching hook finished' },
  HOST_HOOK_START: { name: 'Instance hook started' },
  HOST_HOOK_FINISH: { name: 'Instance hook finished' },
};

export const ACTIONS = {
  list: { name: 'List' },
  get: { name: 'Get' },
  create: { name: 'Create' },
  update: { name: 'Update' },
  delete: { name: 'Delete' },
  execute: { name: 'Execute' },
  any: { name: 'Any' },
};

export const RESOURCES = {
  activation: { name: 'Activation' },
  customer: { name: 'Customer' },
  event: { name: 'Event' },
  machine: { name: 'Machine' },
  notification_group: { name: 'Notification Group' },
  pipeline: { name: 'Pipeline' },
  plan: { name: 'Plan' },
  report: { name: 'Report' },
  update_log: { name: 'Update Log' },
  data_report: { name: 'Data Report' },
  action_link: { name: 'Action Link' },
  api_key: { name: 'Api key' },
  role: { name: 'Role' },
  role_binding: { name: 'Role Binding' },
  installer_key: { name: 'Installer Key' },
  any: { name: 'Any' },
};

export const NOTIFICATION_LEVELS_MAPPING = {
  INFO: { name: 'Info' },
  ERROR: { name: 'Error' },
  SUCCESS: { name: 'Success' },
  WARN: { name: 'Warning' },
  DEBUG: { name: 'Debug' },
};

export const CRON_PRESETS = {
  minutesSelect: [
    { value: '0', label: '0' },
    { value: '5', label: '5' },
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '20', label: '20' },
    { value: '25', label: '25' },
    { value: '30', label: '30' },
    { value: '35', label: '35' },
    { value: '40', label: '40' },
    { value: '45', label: '45' },
    { value: '50', label: '50' },
    { value: '55', label: '55' },
  ],
  hoursSelect: [
    { value: '0', label: '12 AM' },
    { value: '1', label: '1 AM' },
    { value: '2', label: '2 AM' },
    { value: '3', label: '3 AM' },
    { value: '4', label: '4 AM' },
    { value: '5', label: '5 AM' },
    { value: '6', label: '6 AM' },
    { value: '7', label: '7 AM' },
    { value: '8', label: '8 AM' },
    { value: '9', label: '9 AM' },
    { value: '10', label: '10 AM' },
    { value: '11', label: '11 AM' },
    { value: '12', label: '12 PM' },
    { value: '13', label: '1 PM' },
    { value: '14', label: '2 PM' },
    { value: '15', label: '3 PM' },
    { value: '16', label: '4 PM' },
    { value: '17', label: '5 PM' },
    { value: '18', label: '6 PM' },
    { value: '19', label: '7 PM' },
    { value: '20', label: '8 PM' },
    { value: '21', label: '9 PM' },
    { value: '22', label: '10 PM' },
    { value: '23', label: '11 PM' },
  ],
  monthsSelect: [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ],
  hours: [
    { value: '*/3', label: 'Every 3 hours' },
    { value: '*/6', label: 'Every 6 Hours' },
    { value: '*/12', label: 'Every 12 Hours' },
  ],

  days: [
    { value: '*', label: 'Every Day' },
    { value: '*/2', label: 'Even Days' },
    { value: '1-31/2', label: 'Odd Days' },
    { value: '*/5', label: 'Every 5 Days' },
    { value: '*/7', label: 'Every 7 Days' },
  ],

  months: [
    { value: '*', label: 'Every Month' },
    { value: '1-12/2', label: 'Odd Months' },
    { value: '*/2', label: 'Every 2 Months' },
    { value: '*/3', label: 'Every 3 Months' },
    { value: '*/6', label: 'Every 6 Months' },
  ],
  weekdays: [
    { value: '*', label: 'Every Weekday' },
    { value: '0', label: 'Sun' },
    { value: '1', label: 'Mon' },
    { value: '2', label: 'Tue' },
    { value: '3', label: 'Wed' },
    { value: '4', label: 'Thu' },
    { value: '5', label: 'Fri' },
    { value: '6', label: 'Sat' },
    { value: '1-5', label: 'Mon-Fri' },
    { value: '0,6', label: 'Weekend' },
  ],
};

export const DRY_RUN_STATES = ['true', 'false'];

export const pageSize = 1000;
export const pageSizeForPlans = 200;
